/**
 * One-time migration utility to rename localStorage keys from wormble- to wordpop-
 * This preserves all user data during the WordPop rebrand
 */

const MIGRATION_FLAG = 'wordpop-migration-complete';
const CLEANUP_VERSION = 'wordpop-cleanup-v2'; // Versioned cleanup for orphaned keys

/**
 * Check if migration is needed
 */
export function shouldMigrate(): boolean {
  return localStorage.getItem(MIGRATION_FLAG) !== 'true';
}

/**
 * Migrate all localStorage keys from wormble- prefix to wordpop- prefix
 * This runs once and preserves all user game progress, settings, and analytics
 */
export function migrateStorageKeys(): void {
  // Skip if already migrated
  if (!shouldMigrate()) return;
  
  try {
    let migratedCount = 0;
    
    // Get all keys that need migration (snapshot to avoid issues during iteration)
    const allKeys = Object.keys(localStorage);
    
    // Migrate lowercase wormble- keys
    allKeys.forEach(key => {
      if (key.startsWith('wormble-')) {
        const newKey = key.replace('wormble-', 'wordpop-');
        const value = localStorage.getItem(key);
        if (value !== null) {
          localStorage.setItem(newKey, value);
          localStorage.removeItem(key);
          migratedCount++;
        }
      }
    });
    
    // Also migrate Wormble- prefixed keys (capital W, used in some scans)
    allKeys.forEach(key => {
      if (key.startsWith('Wormble-')) {
        const newKey = key.replace('Wormble-', 'wordpop-');
        const value = localStorage.getItem(key);
        if (value !== null) {
          localStorage.setItem(newKey, value);
          localStorage.removeItem(key);
          migratedCount++;
        }
      }
    });
    
    // Mark migration as complete
    localStorage.setItem(MIGRATION_FLAG, 'true');
    
    if (migratedCount > 0) {
      console.log(`📦 WordPop storage migration complete: ${migratedCount} keys migrated`);
    }
  } catch (error) {
    console.error('Storage migration failed:', error);
    // Still mark as complete to avoid repeated failed attempts
    localStorage.setItem(MIGRATION_FLAG, 'true');
  }
}

/**
 * Check if cleanup is needed (runs independently of initial migration)
 */
export function shouldRunCleanup(): boolean {
  return localStorage.getItem(CLEANUP_VERSION) !== 'true';
}

/**
 * Cleanup any remaining wormble- keys that slipped through
 * This catches keys written by old code after initial migration
 */
export function cleanupOrphanedKeys(): void {
  if (!shouldRunCleanup()) return;
  
  try {
    let cleanedCount = 0;
    const allKeys = Object.keys(localStorage);
    
    allKeys.forEach(key => {
      // Migrate any remaining wormble- keys (case-insensitive)
      const lowerKey = key.toLowerCase();
      if (lowerKey.startsWith('wormble-') || lowerKey.startsWith('wormble_')) {
        const newKey = key.replace(/^wormble[-_]/i, 'wordpop-');
        const value = localStorage.getItem(key);
        
        if (value !== null) {
          // Only set new key if it doesn't already exist (avoid overwriting)
          if (localStorage.getItem(newKey) === null) {
            localStorage.setItem(newKey, value);
          }
          localStorage.removeItem(key);
          cleanedCount++;
        }
      }
    });
    
    // Mark cleanup as complete
    localStorage.setItem(CLEANUP_VERSION, 'true');
    
    if (cleanedCount > 0) {
      console.log(`🧹 WordPop cleanup complete: ${cleanedCount} orphaned keys migrated`);
    }
  } catch (error) {
    console.error('Storage cleanup failed:', error);
    localStorage.setItem(CLEANUP_VERSION, 'true');
  }
}

/**
 * Force cleanup - bypasses version check (for admin debugging)
 * Returns details about what was found/cleaned
 */
export function forceCleanupOrphanedKeys(): { cleanedCount: number; keysFound: string[] } {
  const allKeys = Object.keys(localStorage);
  const keysFound: string[] = [];
  let cleanedCount = 0;
  
  allKeys.forEach(key => {
    const lowerKey = key.toLowerCase();
    if (lowerKey.startsWith('wormble-') || lowerKey.startsWith('wormble_')) {
      keysFound.push(key);
      const newKey = key.replace(/^wormble[-_]/i, 'wordpop-');
      const value = localStorage.getItem(key);
      
      if (value !== null) {
        if (localStorage.getItem(newKey) === null) {
          localStorage.setItem(newKey, value);
        }
        localStorage.removeItem(key);
        cleanedCount++;
      }
    }
  });
  
  // Update version flag
  localStorage.setItem(CLEANUP_VERSION, 'true');
  
  console.log(`🧹 Force cleanup: Found ${keysFound.length} keys, cleaned ${cleanedCount}`);
  
  return { cleanedCount, keysFound };
}
